import { useEffect, useRef, useState } from 'preact/hooks';
import type { Effort, ProviderId, Theme } from '../types';
import type { ModelOption } from '../ai/provider';
import type { FreeQuota } from '../ai/openrouter';
import { PROVIDERS, providerMeta } from '../ai/catalog';
import { apiKeys, currentModel, modelListStatus, modelOptions, settings, showToast, ui, updateUi } from '../state/store';
import { eraseEverything, exportData, importData, refreshModels, setApiKey, setKeyStorage, setModel, updateSettings } from '../state/actions';
import { IconEye, IconEyeOff, IconRefresh, IconX } from './Icons';

const CUSTOM = '__custom__';

function KeyField({ provider }: { provider: ProviderId }) {
  const meta = providerMeta(provider);
  const [visible, setVisible] = useState(false);
  const [draft, setDraft] = useState(apiKeys.value[provider] ?? '');
  useEffect(() => setDraft(apiKeys.value[provider] ?? ''), [provider]);
  const commit = () => {
    if (draft !== (apiKeys.value[provider] ?? '')) setApiKey(provider, draft);
  };
  return (
    <div class="field">
      <label for="api-key">{meta.label} API key</label>
      <div class="key-row">
        <input
          id="api-key"
          type={visible ? 'text' : 'password'}
          autocomplete="off"
          spellcheck={false}
          placeholder={meta.keyPlaceholder}
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
        />
        <button type="button" class="icon-button" title={visible ? 'Hide the key' : 'Show the key'} aria-label="Toggle key visibility" onClick={() => setVisible(!visible)}>
          {visible ? <IconEyeOff /> : <IconEye />}
        </button>
      </div>
      <p class="help">
        {meta.description}{' '}
        {meta.keyHelpUrl && (
          <a href={meta.keyHelpUrl} target="_blank" rel="noopener noreferrer">
            Get a key
          </a>
        )}
      </p>
      <div class="radio-row" role="radiogroup" aria-label="Where to keep the key">
        <label>
          <input type="radio" name="keyStorage" checked={settings.value.keyStorage === 'local'} onChange={() => setKeyStorage('local')} />
          Remember on this device
        </label>
        <label>
          <input type="radio" name="keyStorage" checked={settings.value.keyStorage === 'session'} onChange={() => setKeyStorage('session')} />
          This browser tab only
        </label>
      </div>
    </div>
  );
}

const byLabel = (a: ModelOption, b: ModelOption) => a.label.localeCompare(b.label);

function ModelField({ provider }: { provider: ProviderId }) {
  const meta = providerMeta(provider);
  const fetched = modelOptions.value[provider];
  const model = currentModel.value;
  const loading = modelListStatus.value === 'loading';
  // Cue proposes changes through a tool, so models that cannot call tools are left out.
  const all = (fetched && fetched.length > 0 ? fetched : meta.staticModels).filter((m) => m.tools !== false);
  const free = all.filter((m) => m.free).sort(byLabel);
  const paid = all.filter((m) => !m.free);
  const listed = all.some((o) => o.id === model);
  const [custom, setCustom] = useState(!listed);
  useEffect(() => setCustom(!all.some((o) => o.id === currentModel.value)), [provider, fetched?.length]);

  // OpenRouter's catalog is public, so it can load as soon as the field shows.
  useEffect(() => {
    if (provider === 'openrouter' && !fetched) void refreshModels('openrouter', true);
  }, [provider]);

  // The free allowance depends on the account (50 or 1,000 a day), so ask OpenRouter instead of guessing.
  const key = provider === 'openrouter' ? (apiKeys.value.openrouter ?? '').trim() : '';
  const [quota, setQuota] = useState<FreeQuota | null>(null);
  useEffect(() => {
    setQuota(null);
    if (!key) return;
    const controller = new AbortController();
    void import('../ai/openrouter')
      .then(({ loadFreeQuota }) => loadFreeQuota(key, controller.signal))
      .then(setQuota)
      .catch(() => undefined);
    return () => controller.abort();
  }, [key]);

  if (provider === 'demo') return null;
  const option = (o: ModelOption) => (
    <option key={o.id} value={o.id}>
      {o.label}
      {o.contextLength ? ` · ${Math.round(o.contextLength / 1000)}k context` : ''}
    </option>
  );
  return (
    <div class="field">
      <label for="model">Model</label>
      <div class="key-row">
        <select
          id="model"
          value={custom ? CUSTOM : model}
          onChange={(e) => {
            const value = (e.target as HTMLSelectElement).value;
            if (value === CUSTOM) setCustom(true);
            else {
              setCustom(false);
              setModel(provider, value);
            }
          }}
        >
          {free.length > 0 ? (
            <>
              <optgroup label="Free">{free.map(option)}</optgroup>
              <optgroup label={provider === 'openrouter' ? 'Paid (needs credits)' : 'Models'}>{paid.map(option)}</optgroup>
            </>
          ) : (
            paid.map(option)
          )}
          <option value={CUSTOM}>Custom model id…</option>
        </select>
        <button
          type="button"
          class="icon-button"
          title="Load the full model list from the provider"
          aria-label="Refresh models"
          disabled={loading}
          onClick={() => void refreshModels(provider)}
        >
          <IconRefresh class={loading ? 'spin' : ''} />
        </button>
      </div>
      {custom && (
        <input type="text" aria-label="Custom model id" placeholder="Exact model id" value={model} onInput={(e) => setModel(provider, (e.target as HTMLInputElement).value)} />
      )}
      <p class="help">
        {provider === 'openrouter'
          ? fetched
            ? `${all.length} models that can use tools, ${free.length} of them free. ${
                quota
                  ? `This key has ${(quota.limit - quota.used).toLocaleString()} of ${quota.limit.toLocaleString()} free requests left today.`
                  : 'Free models allow 50 requests a day, or 1,000 once you have bought $10 of credits.'
              } Some free models only run if your OpenRouter privacy settings allow providers that train on prompts; the free router picks one that works.`
            : 'Loading the live list from OpenRouter…'
          : fetched
            ? `${all.length} models loaded from ${meta.label}.`
            : 'A short built-in list. Refresh to load every model your key can use.'}
      </p>
    </div>
  );
}

const EFFORTS: { id: Effort; label: string }[] = [
  { id: 'low', label: 'Quick' },
  { id: 'medium', label: 'Balanced' },
  { id: 'high', label: 'Deep' },
];

export function SettingsDialog() {
  const open = ui.value.settingsOpen;
  const ref = useRef<HTMLDialogElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const current = settings.value;
  const meta = providerMeta(current.provider);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = () => updateUi({ settingsOpen: false });

  const onExport = async () => {
    const blob = await exportData();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cue-cards-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file: File | undefined) => {
    if (!file) return;
    try {
      await importData(file);
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    }
  };

  return (
    <dialog ref={ref} class="dialog settings-dialog" aria-labelledby="settings-title" onClose={close} onCancel={close}>
      <form method="dialog" class="dialog-body">
        <header class="dialog-header">
          <h2 id="settings-title">Settings</h2>
          <button type="button" class="icon-button" aria-label="Close" onClick={close}>
            <IconX />
          </button>
        </header>

        <section>
          <h3>Model</h3>
          <div class="provider-grid" role="radiogroup" aria-label="Provider">
            {PROVIDERS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={current.provider === p.id}
                class={`provider-card${current.provider === p.id ? ' active' : ''}`}
                onClick={() => updateSettings({ provider: p.id })}
              >
                <strong>{p.label}</strong>
                <span>{p.needsKey ? (apiKeys.value[p.id] ? 'Key saved' : 'Needs a key') : 'No key needed'}</span>
              </button>
            ))}
          </div>
          {meta.needsKey && <KeyField provider={current.provider} />}
          <ModelField provider={current.provider} />
          {current.provider !== 'demo' && (
            <div class="field">
              <span class="field-label" id="effort-label">
                Thinking
              </span>
              <div class="segmented" role="radiogroup" aria-labelledby="effort-label">
                {EFFORTS.map((e) => (
                  <button key={e.id} type="button" role="radio" aria-checked={current.effort === e.id} class={current.effort === e.id ? 'active' : ''} onClick={() => updateSettings({ effort: e.id })}>
                    {e.label}
                  </button>
                ))}
              </div>
              <p class="help">How long the model thinks before it answers. Deep is better on hard problems, and slower and pricier.</p>
            </div>
          )}
        </section>

        <section>
          <h3>Assistant</h3>
          <label class="check-row">
            <input type="checkbox" checked={current.suggestions} onChange={(e) => updateSettings({ suggestions: (e.target as HTMLInputElement).checked })} />
            <span>
              Suggest next cues
              <span class="help">After each reply, ask the model for three prompts that fit what you are doing. One small extra request each time.</span>
            </span>
          </label>
          <div class="field">
            <label for="system-prompt">Standing instructions</label>
            <textarea
              id="system-prompt"
              rows={3}
              placeholder="For example: prefer type hints, keep answers short, I am learning Rust"
              value={current.systemPrompt}
              onInput={(e) => updateSettings({ systemPrompt: (e.target as HTMLTextAreaElement).value })}
            />
          </div>
        </section>

        <section>
          <h3>Appearance</h3>
          <div class="segmented" role="group" aria-label="Theme">
            {(['system', 'light', 'dark'] as Theme[]).map((theme) => (
              <button key={theme} type="button" class={current.theme === theme ? 'active' : ''} aria-pressed={current.theme === theme} onClick={() => updateSettings({ theme })}>
                {theme[0]!.toUpperCase() + theme.slice(1)}
              </button>
            ))}
          </div>
        </section>

        <section>
          <h3>Your cards</h3>
          <p class="help">Cue has no backend. Cards live in this browser, and your key goes only to the provider you chose.</p>
          <div class="button-row">
            <button type="button" class="button small" onClick={() => void onExport()}>
              Export cards
            </button>
            <button type="button" class="button small" onClick={() => fileRef.current?.click()}>
              Import cards
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              hidden
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                void onImport(input.files?.[0]);
                input.value = '';
              }}
            />
            <button
              type="button"
              class="button small danger"
              onClick={() => {
                if (window.confirm('Delete every card, setting and key Cue keeps in this browser?')) void eraseEverything();
              }}
            >
              Erase everything
            </button>
          </div>
        </section>

        <footer class="dialog-footer">
          <span class="about">
            Cue by{' '}
            <a href="https://nothans.com" target="_blank" rel="noopener noreferrer">
              NotHans
            </a>
          </span>
          <div class="spacer" />
          <button type="button" class="button primary" onClick={close}>
            Done
          </button>
        </footer>
      </form>
    </dialog>
  );
}
